// src/components/ZoomHowToModal.tsx
import React from 'react';

type Props = {
  open: boolean;
  onClose: () => void;
};

const ZoomHowToModal: React.FC<Props> = ({ open, onClose }) => {
  const [tab, setTab] = React.useState<'jp' | 'zh'>('jp');
  const isJP = tab === 'jp';

  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  const jp = [`${base}/images/zoom_step1_jp.png`, `${base}/images/zoom_step2_jp.png`];
  const zh = [`${base}/images/zoom_step1_zh.png`, `${base}/images/zoom_step2_zh.png`];


  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const titleId = 'zoom-howto-title';

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-2 sm:p-4"
      onClick={onClose} 
      aria-labelledby={titleId}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative bg-white w-[96vw] max-w-[1100px] max-h-[92vh] rounded-3xl shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >

        <div className="sticky top-0 z-20 bg-white border-b rounded-t-3xl">
          <div className="flex items-start sm:items-center justify-between gap-2 px-4 sm:px-5 py-3">
            <div className="flex-1 min-w-0">
              <h2 id={titleId} className="text-lg sm:text-xl font-semibold truncate">
                {isJP ? 'Zoom参加手順（ブラウザ）' : 'Zoom 加入步骤（浏览器）'}
              </h2>
              <div className="mt-2 inline-flex rounded-lg bg-gray-100 p-1">
                <button
                  type="button"
                  onClick={() => setTab('jp')}
                  className={`px-3 py-1 rounded-md text-sm ${
                    isJP ? 'bg-white shadow border' : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  日本語
                </button>
                <button
                  type="button"
                  onClick={() => setTab('zh')}
                  className={`px-3 py-1 rounded-md text-sm ${
                    !isJP ? 'bg-white shadow border' : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  中文
                </button>
              </div>
            </div>

            <button
              onClick={onClose}
              className="shrink-0 rounded-full w-10 h-10 flex items-center justify-center hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-coral"
              aria-label={isJP ? '閉じる' : '关闭'}
              type="button"
              title={isJP ? '閉じる' : '关闭'}
            >
              ✕
            </button>
          </div>

          <div className="px-4 sm:px-5 py-3 bg-amber-50 border-t border-amber-100 text-[13px] sm:text-[14px] text-amber-900 leading-relaxed">
            {isJP ? (
              <>
                <b>ポイント：</b> ブラウザが「Zoomアプリを開きますか？」と表示したら
                <b>「キャンセル」</b>を選び、ページ内の<b>「ブラウザから参加」</b>をクリック。
                （Chrome 例：①キャンセル → ②ブラウザから参加 → ③マイク/カメラは許可）
              </>
            ) : (
              <>
                <b>要点：</b> 如果浏览器弹出“是否打开 Zoom 应用”，请先点<b>“取消”</b>，
                然后点击页面上的<b>“从浏览器加入”</b>。（Chrome 示例：①取消 → ②从浏览器加入 → ③允许麦克风/摄像头）
              </>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 sm:px-5 py-4 space-y-5">
          {(isJP ? jp : zh).map((src, i) => (
            <div
              key={src}
              className="rounded-2xl border border-gray-200 overflow-hidden bg-white shadow-sm"
            >
              <img
                src={src}
                alt={isJP ? `手順 ${i + 1}` : `步骤 ${i + 1}`}
                className="block w-full h-auto"
                loading="lazy"
              />
            </div>
          ))}
        </div>

        <div className="sticky bottom-0 z-20 bg-white border-t px-4 sm:px-5 py-3 flex justify-end rounded-b-3xl">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-lg bg-coral text-white hover:opacity-90 text-sm sm:text-base"
          >
            {isJP ? '分かった' : '我已了解'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ZoomHowToModal;
